import { saveAs } from 'file-saver';
import { Font, Workbook, Worksheet } from 'exceljs';
import { EXCEL_MIME_FILE_TYPE } from 'constants/common/excel';
import { ExcelCellValue, ExcelColumn, ExcelMultiDataSet } from 'types/common/excel';
import _chunk from 'lodash/chunk';

// [EN] SheetJS wrote this exact font as the workbook default, so react-export-excel output opened
//      at size 12. exceljs defaults to the same font at size 11, which would silently shrink every
//      sheet we already ship — hence the explicit font rather than relying on either default.
// [KO] SheetJS 가 워크북 기본 폰트로 정확히 이것을 썼기 때문에 react-export-excel 산출물은 12 로
//      열렸다. exceljs 의 기본은 같은 폰트의 11 이라, 그대로 두면 기존 시트가 조용히 작아진다.
//      그래서 어느 쪽 기본값에도 기대지 않고 폰트를 명시한다.
const DEFAULT_FONT: Partial<Font> = {
  name: 'Calibri',
  size: 12,
  family: 2,
  scheme: 'minor',
  color: { theme: 1 },
};

// [EN] Max digit width. SheetJS' writer defaults to 6 (`DEF_MDW`), and the widths in the templates
//      were tuned against that, so the same constant has to drive the conversion here.
// [KO] 최대 숫자 너비. SheetJS writer 의 기본값이 6(`DEF_MDW`)이고 템플릿의 너비 값이 그 기준으로
//      맞춰져 있으므로, 여기서도 같은 상수를 써야 한다.
const MAX_DIGIT_WIDTH = 6;

// [EN] react-export-excel took column width in pixels (`widthPx`) and handed it to SheetJS, which
//      converted px → characters → the stored OOXML width. exceljs instead writes `column.width`
//      straight into the XML, so both steps have to be reproduced here — doing only the first one
//      drops the padding SheetJS adds and every column comes out narrower.
// [KO] react-export-excel 은 컬럼 너비를 픽셀(`widthPx`)로 받아 SheetJS 에 넘겼고, SheetJS 가
//      px → 문자 → OOXML 저장 너비로 두 번 변환했다. exceljs 는 `column.width` 를 XML 에 그대로
//      쓰므로 두 단계를 모두 재현해야 한다. 첫 단계만 하면 SheetJS 가 더하는 패딩이 빠져
//      모든 컬럼이 좁아진다.
const pixelToCharWidth = (px: number): number =>
  Math.floor(((px - 5) / MAX_DIGIT_WIDTH) * 100 + 0.5) / 100;

const charWidthToStoredWidth = (chr: number): number =>
  Math.round(((chr * MAX_DIGIT_WIDTH + 5) / MAX_DIGIT_WIDTH) * 256) / 256;

export const pixelToStoredWidth = (px: number): number =>
  charWidthToStoredWidth(pixelToCharWidth(px));

const isExcelColumn = (column: ExcelCellValue | ExcelColumn): column is ExcelColumn =>
  typeof column === 'object' && column !== null && !(column instanceof Date) && 'value' in column;

const addRow = (worksheet: Worksheet, values: ExcelCellValue[]): void => {
  const row = worksheet.addRow(values);
  row.font = DEFAULT_FONT;
};

export const saveWorkbook = async (workbook: Workbook, fileName: string): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: EXCEL_MIME_FILE_TYPE }), `${fileName}.xlsx`);
};

// [EN] A multi data set is a list of blocks stacked top to bottom on one sheet: each block writes
//      its `columns` as a row, then its `data` rows. That is what react-export-excel did with
//      `<ExcelSheet dataSet={...} />`, and the revenue templates rely on the stacking to put a
//      band-label row above the real header row.
// [KO] multi data set 은 한 시트에 위에서 아래로 쌓이는 블록 목록이다. 블록마다 `columns` 를 한 행으로
//      쓰고 이어서 `data` 행들을 쓴다. react-export-excel 의 `<ExcelSheet dataSet={...} />` 가 하던
//      동작이고, revenue 템플릿은 이 쌓임을 이용해 실제 헤더 위에 구분 밴드 행을 올린다.
const writeMultiDataSet = (worksheet: Worksheet, multiDataSet: ExcelMultiDataSet[]): void => {
  multiDataSet.forEach(({ columns, data }) => {
    columns.forEach((column, index) => {
      if (!isExcelColumn(column) || column.widthPx === undefined) {
        return;
      }
      worksheet.getColumn(index + 1).width = pixelToStoredWidth(column.widthPx);
    });
    addRow(
      worksheet,
      columns.map(column => (isExcelColumn(column) ? column.value : column)),
    );
    data.forEach(row => addRow(worksheet, row));
  });
};

interface MultiDataSetProps {
  fileName: string;
  sheetName: string;
  multiDataSet: ExcelMultiDataSet[];
}

export const downloadMultiDataSetExcel = async ({
  fileName,
  sheetName,
  multiDataSet,
}: MultiDataSetProps): Promise<void> => {
  const workbook = new Workbook();
  writeMultiDataSet(workbook.addWorksheet(sheetName), multiDataSet);
  await saveWorkbook(workbook, fileName);
};

interface TableProps<T> {
  fileName: string;
  sheetName: string;
  exportData: T[];
  /** Rows per sheet. Splitting keeps a single sheet from growing past what Excel opens comfortably. */
  chunkSize: number;
}

export const downloadTableExcel = async <T extends object>({
  fileName,
  sheetName,
  exportData,
  chunkSize,
}: TableProps<T>): Promise<void> => {
  // [EN] Empty input is a no-op, not a crash: the column list is read off the first row, so the
  //      caller would otherwise have to hold this contract. `noUncheckedIndexedAccess` is off, so
  //      `tsc` types that first row as `T` and would not flag a new call site that skips the check.
  // [KO] 입력이 비면 크래시가 아니라 no-op 이다. 컬럼 목록을 첫 행에서 뽑기 때문에, 이 가드가 없으면
  //      호출부가 계약을 들고 있어야 한다. `noUncheckedIndexedAccess` 가 꺼져 있어 `tsc` 는 첫 행을
  //      `T` 로 잡고, 검사를 빠뜨린 새 호출부를 잡아주지 못한다.
  if (exportData.length === 0) {
    return;
  }

  const workbook = new Workbook();
  _chunk(exportData, chunkSize).forEach((pageData, index) => {
    const worksheet = workbook.addWorksheet(`${sheetName}_${index}`);
    const keys = Object.keys(pageData[0]);
    addRow(worksheet, keys);
    pageData.forEach(row =>
      addRow(
        worksheet,
        keys.map(key => (row as Record<string, ExcelCellValue>)[key]),
      ),
    );
  });
  await saveWorkbook(workbook, fileName);
};
